import { useCallback, useEffect, useState } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import { ArrowLeft, ArrowRight, Quote, Star } from 'lucide-react';
import { useLang } from '@/i18n/LanguageContext';
import SectionHead from '@/components/SectionHead';
import { Reveal } from '@/components/Reveal';

export default function Testimonials() {
  const { content } = useLang();
  const t = content.testimonials;
  const [emblaRef, embla] = useEmblaCarousel({ loop: true, align: 'start' });
  const [selected, setSelected] = useState(0);

  const onSelect = useCallback(() => {
    if (embla) setSelected(embla.selectedScrollSnap());
  }, [embla]);

  useEffect(() => {
    if (!embla) return;
    embla.on('select', onSelect);
    onSelect();
  }, [embla, onSelect]);

  return (
    <section className="py-20 lg:py-28" data-testid="testimonials-section">
      <div className="container-x">
        <SectionHead eyebrow={t.eyebrow} title={t.title} subtitle={t.subtitle} />
        <Reveal delay={0.15}>
          <div className="mt-12 overflow-hidden" ref={emblaRef}>
            <div className="flex gap-5">
              {t.items.map((item, i) => (
                <article key={i} data-testid={`testimonial-card-${i}`} className="card-soft min-w-0 flex-[0_0_88%] p-7 sm:flex-[0_0_48%] lg:flex-[0_0_36%]">
                  <div className="flex items-center justify-between">
                    <Quote className="h-6 w-6 text-brand-cyan" />
                    <span className="flex gap-0.5" data-testid={`testimonial-stars-${i}`} aria-label={`${item.rating}/5`}>
                      {Array.from({ length: item.rating || 5 }).map((_, s) => (
                        <Star key={s} className="h-4 w-4 fill-amber-400 text-amber-400" />
                      ))}
                    </span>
                  </div>
                  <p className="mt-4 min-h-[128px] text-[15px] leading-relaxed text-brand-navy/90">“{item.text}”</p>
                  <footer className="mt-6 border-t border-brand-line pt-5">
                    <p className="text-sm font-semibold text-brand-navy">{item.author}</p>
                    <p className="mt-0.5 text-xs text-brand-muted">{item.role} · {item.company} · {item.city}</p>
                  </footer>
                </article>
              ))}
            </div>
          </div>
        </Reveal>
        <div className="mt-8 flex items-center gap-3">
          <button data-testid="testimonial-prev-btn" onClick={() => embla && embla.scrollPrev()} aria-label="Anterior" className="flex h-11 w-11 items-center justify-center rounded-full border border-brand-line text-brand-navy transition hover:border-brand-blue hover:text-brand-blue">
            <ArrowLeft className="h-4 w-4" />
          </button>
          <button data-testid="testimonial-next-btn" onClick={() => embla && embla.scrollNext()} aria-label="Próximo" className="flex h-11 w-11 items-center justify-center rounded-full border border-brand-line text-brand-navy transition hover:border-brand-blue hover:text-brand-blue">
            <ArrowRight className="h-4 w-4" />
          </button>
          <div className="ml-3 flex gap-1.5">
            {t.items.map((_, i) => (
              <button key={i} data-testid={`testimonial-dot-${i}`} onClick={() => embla && embla.scrollTo(i)} aria-label={`Depoimento ${i + 1}`} className={`h-1.5 rounded-full transition-all ${i === selected ? 'w-7 bg-brand-blue' : 'w-1.5 bg-brand-line'}`} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}